<template>
  <div v-if="show" class="peek-modal-backdrop" @click.self="close">
    <div class="peek-modal-dialog">
      <div class="peek-modal-content">
        <!-- Header -->
        <div class="peek-modal-header">
          <div class="peek-header-left">
            <i class="bi bi-lightning-charge-fill peek-header-icon"></i>
            <h5 class="peek-modal-title">{{ execution ? execution.workflow_name : 'Loading...' }}</h5>
            <StatusBadge v-if="execution" :status="execution.status" size="small" />
          </div>
          <div class="peek-header-right">
            <button 
              v-if="execution"
              @click="openFullDetails" 
              class="peek-btn-full"
              title="Open full details"
            >
              <i class="bi bi-arrows-fullscreen"></i>
              Full Details
            </button>
            <button type="button" class="peek-btn-close" @click="close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="peek-loading">
          <div class="peek-spinner"></div>
          <p>Loading execution details...</p>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="peek-error">
          <i class="bi bi-exclamation-triangle-fill"></i>
          <p>{{ error }}</p>
          <button @click="loadDetails" class="peek-btn-retry">Try Again</button>
        </div>

        <!-- Content -->
        <div v-else-if="execution" class="peek-modal-body">
          <!-- Info Grid -->
          <div class="peek-info-grid">
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-fingerprint"></i> Execution UUID
              </span>
              <span class="peek-info-value mono">
                {{ execution.execution_uuid }}
                <CopyButton :text="execution.execution_uuid" />
              </span>
            </div>
            <div v-if="execution.context_uuid" class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-folder2"></i> Context UUID
              </span>
              <span class="peek-info-value mono">
                {{ truncateUuid(execution.context_uuid, 20) }}
                <CopyButton :text="execution.context_uuid" />
              </span>
            </div>
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-tag"></i> Type
              </span>
              <span class="peek-info-value">
                <span class="peek-type-badge" :class="execution.type">{{ execution.type || 'N/A' }}</span>
              </span>
            </div>
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-hdd-stack"></i> Environment
              </span>
              <span class="peek-info-value">
                <span :class="`peek-env-badge peek-env-${execution.env}`">{{ execution.env ? execution.env.toUpperCase() : 'N/A' }}</span>
              </span>
            </div>
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-play-circle"></i> Started
              </span>
              <span class="peek-info-value">{{ formatTimestamp(execution.start_time) }}</span>
            </div>
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-stop-circle"></i> Ended
              </span>
              <span class="peek-info-value">{{ execution.end_time ? formatTimestamp(execution.end_time) : '—' }}</span>
            </div>
            <div class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-clock-history"></i> Duration
              </span>
              <span class="peek-info-value mono">{{ formatDuration(execution.duration) }}</span>
            </div>
            <div v-if="execution.ref" class="peek-info-item">
              <span class="peek-info-label">
                <i class="bi bi-link-45deg"></i> Ref
              </span>
              <span class="peek-info-value mono">{{ execution.ref }}</span>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="execution.error_message" class="peek-section peek-error-section">
            <div class="peek-section-header">
              <h6><i class="bi bi-exclamation-triangle-fill"></i> Error</h6>
            </div>
            <pre class="peek-error-message">{{ execution.error_message }}</pre>
          </div>

          <!-- Input Data -->
          <div v-if="execution.input_data && Object.keys(execution.input_data).length" class="peek-section">
            <div class="peek-section-header" @click="sections.input = !sections.input">
              <h6>
                <i class="bi bi-chevron-right" :class="{ 'rotated': sections.input }"></i>
                <i class="bi bi-braces"></i> Input Data
              </h6>
            </div>
            <div v-if="sections.input" class="peek-section-body">
              <pre class="peek-json">{{ formatJson(execution.input_data) }}</pre>
            </div>
          </div>

          <!-- Nodes Summary -->
          <div v-if="flattenedNodes.length" class="peek-section">
            <div class="peek-section-header" @click="sections.nodes = !sections.nodes">
              <h6>
                <i class="bi bi-chevron-right" :class="{ 'rotated': sections.nodes }"></i>
                <i class="bi bi-diagram-3"></i> Nodes
                <span class="peek-badge">{{ totalNodeCount }}</span>
              </h6>
              <div class="peek-node-stats">
                <span v-if="nodeStatusCounts.completed" class="peek-stat-pill completed">
                  <i class="bi bi-check-circle-fill"></i> {{ nodeStatusCounts.completed }}
                </span>
                <span v-if="nodeStatusCounts.errored" class="peek-stat-pill errored">
                  <i class="bi bi-x-circle-fill"></i> {{ nodeStatusCounts.errored }}
                </span>
                <span v-if="nodeStatusCounts.running" class="peek-stat-pill running">
                  <i class="bi bi-arrow-clockwise"></i> {{ nodeStatusCounts.running }}
                </span>
                <span v-if="nodeStatusCounts.skipped" class="peek-stat-pill skipped">
                  <i class="bi bi-skip-forward-circle-fill"></i> {{ nodeStatusCounts.skipped }}
                </span>
              </div>
            </div>
            <div v-if="sections.nodes" class="peek-section-body">
              <div class="peek-nodes-list">
                <div 
                  v-for="item in flattenedNodes" 
                  :key="item.uniqueKey" 
                  class="peek-node-item"
                  :class="{ 'peek-node-error': getNodeStatus(item) === 'errored' || getNodeStatus(item) === 'failed' }"
                  :style="{ paddingLeft: (12 + item.depth * 20) + 'px' }"
                >
                  <span v-if="item.depth > 0" class="peek-node-connector">
                    <i class="bi bi-arrow-return-right"></i>
                  </span>
                  <StatusBadge :status="getNodeStatus(item)" size="small" />
                  <span class="peek-node-name">{{ item.node.name }}</span>
                  <span class="peek-node-type">{{ item.node.type }}</span>
                  <span class="peek-node-duration">{{ getNodeDuration(item) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Logs -->
          <div v-if="execution.logs && execution.logs.length" class="peek-section">
            <div class="peek-section-header" @click="sections.logs = !sections.logs">
              <h6>
                <i class="bi bi-chevron-right" :class="{ 'rotated': sections.logs }"></i>
                <i class="bi bi-terminal"></i> Logs
                <span class="peek-badge">{{ execution.logs.length }}</span>
              </h6>
            </div>
            <div v-if="sections.logs" class="peek-section-body">
              <LogViewer :logs="execution.logs" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import CopyButton from '@/components/workflow/CopyButton.vue';
import LogViewer from '@/components/workflow/LogViewer.vue';
import {
  formatDuration,
  formatDurationShort,
  formatTimestamp,
  truncateUuid
} from '@/shared/workflowHistoryUtils';

export default {
  name: 'WorkflowQuickPeekModal',
  components: {
    StatusBadge,
    CopyButton,
    LogViewer
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    executionUuid: {
      type: String,
      default: null
    },
    executionData: {
      type: Object,
      default: null
    },
    workflowHistoryService: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'history',
      validator: v => ['history', 'running'].includes(v)
    }
  },
  data() {
    return {
      execution: null,
      loading: false,
      error: null,
      sections: {
        input: false,
        nodes: true,
        logs: false
      }
    };
  },
  computed: {
    flattenedNodes() {
      if (!this.execution || !this.execution.execution_nodes) return [];

      const nodes = this.execution.execution_nodes;
      const nodesMap = new Map();
      const rootNodes = [];

      // First pass: create enriched nodes
      nodes.forEach((item, index) => {
        const uniqueKey = `${item.node.id}-${item.parent_node_id || 'root'}-${index}`;
        nodesMap.set(uniqueKey, {
          ...item,
          uniqueKey,
          children: [],
          depth: 0
        });
      });

      // Second pass: build parent-child relationships
      nodes.forEach((item, index) => {
        const uniqueKey = `${item.node.id}-${item.parent_node_id || 'root'}-${index}`;
        const enriched = nodesMap.get(uniqueKey);

        if (item.parent_node_id) {
          let parentNode = null;
          for (const value of nodesMap.values()) {
            if (value.node.id === item.parent_node_id) {
              parentNode = value;
              break;
            }
          }
          if (parentNode) {
            parentNode.children.push(enriched);
            enriched.depth = parentNode.depth + 1;
          } else {
            rootNodes.push(enriched);
          }
        } else {
          rootNodes.push(enriched);
        }
      });

      // Depth-first flatten with time-based sorting at each level
      const sortByTime = (a, b) => {
        const aState = a.node.State || a.node.state || {};
        const bState = b.node.State || b.node.state || {};
        const aTime = aState.starttime || aState.start_time || '';
        const bTime = bState.starttime || bState.start_time || '';
        return new Date(aTime) - new Date(bTime);
      };

      const flatten = (nodeList) => {
        const result = [];
        nodeList.sort(sortByTime).forEach(node => {
          result.push(node);
          if (node.children.length) {
            result.push(...flatten(node.children));
          }
        });
        return result;
      };

      return flatten(rootNodes);
    },
    nodeStatusCounts() {
      const counts = {};
      this.flattenedNodes.forEach(item => {
        const status = this.getNodeStatus(item);
        counts[status] = (counts[status] || 0) + 1;
      });
      return counts;
    },
    totalNodeCount() {
      return this.flattenedNodes.length;
    }
  },
  watch: {
    show(val) {
      if (val) {
        if (this.executionData) {
          this.execution = this.executionData;
        } else if (this.executionUuid) {
          this.loadDetails();
        }
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.handleKeydown);
      } else {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleKeydown);
      }
    }
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    formatDuration,
    formatDurationShort,
    formatTimestamp,
    truncateUuid,

    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    },

    async loadDetails() {
      if (!this.executionUuid) return;

      this.loading = true;
      this.error = null;
      this.execution = null;

      try {
        this.execution = this.mode === 'running'
          ? await this.workflowHistoryService.fetchRunningWorkflowDetails(this.executionUuid)
          : await this.workflowHistoryService.fetchExecutionDetails(this.executionUuid);
      } catch (err) {
        this.error = err.message || 'Failed to load execution details';
        console.error('Error loading execution details:', err);
      } finally {
        this.loading = false;
      }
    },

    openFullDetails() {
      this.close();
      const base = this.mode === 'running' ? '/admin/scout/workflow/running' : '/admin/scout/workflow/history';
      this.$router.push(`${base}/${this.executionUuid}`);
    },

    close() {
      this.$emit('close');
    },

    formatJson(data) {
      try {
        return JSON.stringify(data, null, 2);
      } catch {
        return String(data);
      }
    },

    getNodeStatus(item) {
      const state = item.node.State || item.node.state || {};
      return state.status || 'pending';
    },

    getNodeDuration(item) {
      const state = item.node.State || item.node.state || {};
      const startTime = state.starttime || state.start_time;
      const endTime = state.endtime || state.end_time;
      if (startTime && endTime) {
        const ms = new Date(endTime) - new Date(startTime);
        return formatDurationShort(ms);
      }
      return '—';
    }
  }
};
</script>

<style scoped>
.peek-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  animation: peekFadeIn 0.15s ease;
}

@keyframes peekFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.peek-modal-dialog {
  max-width: 800px;
  width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: peekSlideIn 0.2s ease;
  position: relative;
  z-index: 1000000;
}

@keyframes peekSlideIn {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.peek-modal-content {
  background: var(--bg-card);
  border-radius: 10px;
  border: 1px solid var(--border-default);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.peek-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-default);
  flex-shrink: 0;
}

.peek-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.peek-header-icon {
  font-size: 20px;
  color: var(--primary-start);
  flex-shrink: 0;
}

.peek-modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.peek-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.peek-btn-full {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  color: var(--primary-start);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.peek-btn-full:hover {
  background-color: var(--form-bg);
  border-color: var(--primary-start);
}

.peek-btn-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.peek-btn-close:hover {
  background-color: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

/* Loading */
.peek-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 20px;
  color: #9ca3af;
}

.peek-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: peekSpin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes peekSpin {
  to { transform: rotate(360deg); }
}

/* Error */
.peek-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: #fca5a5;
  text-align: center;
}

.peek-error i {
  font-size: 32px;
  margin-bottom: 12px;
  color: #ef4444;
}

.peek-btn-retry {
  margin-top: 12px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #ef4444;
  border-radius: 6px;
  color: #fca5a5;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.peek-btn-retry:hover {
  background-color: rgba(239, 68, 68, 0.1);
}

/* Body */
.peek-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

/* Info Grid */
.peek-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.peek-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  background-color: var(--form-bg);
  border-radius: 6px;
  border: 1px solid var(--border-default);
}

.peek-info-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.peek-info-label i {
  font-size: 12px;
}

.peek-info-value {
  font-size: 13px;
  color: var(--text-primary);
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 6px;
}

.peek-info-value.mono {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 12px;
}

/* Type Badge */
.peek-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.peek-type-badge.project {
  background-color: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.peek-type-badge.component {
  background-color: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border: 1px solid rgba(139, 92, 246, 0.3);
}

/* Env Badge */
.peek-env-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.peek-env-dev {
  background-color: var(--border-default);
  color: #fbbf24;
  border: 1px solid #fbbf24;
}

.peek-env-stage {
  background-color: var(--border-default);
  color: #fb923c;
  border: 1px solid #fb923c;
}

.peek-env-live {
  background-color: var(--border-default);
  color: #10b981;
  border: 1px solid #10b981;
}

/* Sections */
.peek-section {
  margin-bottom: 12px;
  border: 1px solid var(--border-default);
  border-radius: 6px;
  overflow: hidden;
}

.peek-error-section {
  border-color: rgba(239, 68, 68, 0.3);
}

.peek-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: var(--form-bg);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.peek-section-header:hover {
  background-color: var(--bg-card);
}

.peek-section-header h6 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.peek-section-header h6 .bi-chevron-right {
  transition: transform 0.2s ease;
  font-size: 11px;
}

.peek-section-header h6 .bi-chevron-right.rotated {
  transform: rotate(90deg);
}

.peek-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  padding: 1px 6px;
  background-color: var(--primary-start);
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.peek-section-body {
  padding: 12px 14px;
}

/* Error Message */
.peek-error-message {
  margin: 0;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.08);
  border-radius: 4px;
  color: #fca5a5;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
}

/* JSON */
.peek-json {
  margin: 0;
  padding: 10px;
  background-color: var(--form-bg);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
}

/* Nodes List */
.peek-nodes-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
}

.peek-node-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background-color: var(--form-bg);
  border-radius: 4px;
  border: 1px solid var(--border-default);
  transition: padding-left 0.15s ease;
}

.peek-node-connector {
  color: #4b5563;
  font-size: 12px;
  flex-shrink: 0;
}

.peek-node-name {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.peek-node-type {
  font-size: 11px;
  color: #9ca3af;
  text-transform: capitalize;
  flex-shrink: 0;
}

.peek-node-duration {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
}

.peek-node-error {
  border-color: rgba(239, 68, 68, 0.2);
  background-color: rgba(239, 68, 68, 0.04);
}

/* Node Status Pills */
.peek-node-stats {
  display: flex;
  gap: 6px;
  align-items: center;
}

.peek-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.peek-stat-pill i {
  font-size: 10px;
}

.peek-stat-pill.completed {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.peek-stat-pill.errored,
.peek-stat-pill.failed {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.peek-stat-pill.running {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.peek-stat-pill.skipped {
  background-color: rgba(156, 163, 175, 0.1);
  color: #9ca3af;
}

/* Responsive */
@media (max-width: 640px) {
  .peek-info-grid {
    grid-template-columns: 1fr;
  }

  .peek-modal-dialog {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .peek-modal-content {
    border-radius: 0;
    max-height: 100vh;
  }
}
</style>
